import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AuthenticationProvider } from "nikao-authentication";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Licenses from "./pages/Licenses";
import CertsAndSecrets from "./pages/CertsAndSecrets";
import Categories from "./pages/Categories";
import Reports from "./pages/Reports";
import Audit from "./pages/Audit";
import Users from "./pages/Users";
import NotificationPreferences from "./pages/NotificationPreferences";
import AssetRegister from "./pages/AssetRegister";
import Attestation from "./pages/Attestation";
import SoftwarePermissions from "./pages/SoftwarePermissions";
import CronSchedules from "./pages/CronSchedules";
import SupplierLists from "./pages/SupplierLists";
import Clients from "./pages/Clients";
import AiSystems from "./pages/AiSystems";
import Layout from "./components/Layout";

const msalClientId = process.env.REACT_APP_MSAL_CLIENT_ID;

const msalTenantId = process.env.REACT_APP_MSAL_TENANT_ID;

const missing = [];

if (!msalClientId) missing.push("REACT_APP_MSAL_CLIENT_ID");

if (!msalTenantId) missing.push("REACT_APP_MSAL_TENANT_ID");

if (missing.length > 0) {
  throw new Error(`Missing required MSAL configuration: ${missing.join(", ")}`);
}

const msalAuthority = `https://login.microsoftonline.com/${msalTenantId}`;

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Wait for auth check to complete before making routing decision
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="supplier-lists" element={<SupplierLists />} />
        <Route path="clients" element={<Clients />} />
        <Route path="licenses" element={<Licenses />} />
        <Route path="certs-secrets" element={<CertsAndSecrets />} />
        <Route path="asset-register" element={<AssetRegister />} />
        <Route path="attestation" element={<Attestation />} />
        <Route path="software-permissions" element={<SoftwarePermissions />} />
        <Route path="ai-systems" element={<AiSystems />} />
        <Route path="categories" element={<Categories />} />
        <Route path="reports" element={<Reports />} />
        <Route path="audit" element={<Audit />} />
        <Route path="users" element={<Users />} />
        <Route
          path="notification-preferences"
          element={<NotificationPreferences />}
        />
        <Route path="cron-schedules" element={<CronSchedules />} />
      </Route>
    </Routes>
  );
};

const App = () => {
  return (
    <AuthenticationProvider
      applicationId={msalClientId}
      authority={msalAuthority}
      redirectUri={
        typeof window !== "undefined" ? window.location.origin : undefined
      }
    >
      <AuthProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Box sx={{ minHeight: "100vh" }}>
            <AppRoutes />
          </Box>
        </LocalizationProvider>
      </AuthProvider>
    </AuthenticationProvider>
  );
};

export default App;
